import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";

import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";

import { Button, CircularProgress, Grid } from "@mui/material";

import Modal from "Components/Common/Modal";
import SelectField from "Components/Common/SelectField";
import DatePicker from "Components/Common/DatePicker";
import Backdrop from "Components/Common/Backdrop";

import { useGetDeveloperBuildingsQuery } from "apis/AuditSchedular";
import {
  useGetLazyGetMockDrillDetailsById,
  usePostMockDrillscheduleMutation,
} from "apis/FireSafety/FireSafetyMockDrill/fireSafetyMockDrill";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

const MockDrillScheduleModal = ({ isOpen, handleClose, id, t }) => {
  const {
    data: { data: buildingsData } = {},
    isLoading: isBuildingDataLoading,
  } = useGetDeveloperBuildingsQuery();

  const [scheduleMockDrill, { isLoading: isMockDrillScheduleLoading }] =
    usePostMockDrillscheduleMutation();

  const [
    getMockDrillDetail,
    { data: { data: mockDrillData } = {}, isLoading: isMockDrillDataLoading },
  ] = useGetLazyGetMockDrillDetailsById();

  const updatedBuildingOptions = useMemo(
    () => [
      ...(buildingsData?.map(({ id, name }) => ({ label: name, value: id })) ??
        []),
    ],
    [buildingsData]
  );

  const isFirstHalf = (date) => date && moment(date).month() <= 5; 
  const isSecondHalf = (date) => date && moment(date).month() >= 6; 
  
  const initialValues = useMemo(
    () => ({
      id: id || 0,
      developerBuildingId: mockDrillData?.developerBuildingId ?? "",
      mockDrillConductedOn: mockDrillData?.mockDrillConductedOn
        ? convertUTCDateToLocalDate(mockDrillData?.mockDrillConductedOn)
        : "",
      nextMockDrillDueOn: mockDrillData?.nextMockDrillDueOn
        ? convertUTCDateToLocalDate(mockDrillData?.nextMockDrillDueOn)
        : "",
    }),
    [mockDrillData]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      developerBuildingId: Yup.number().required("Building name is required"),
      mockDrillConductedOn: Yup.date().test(
        "isValidMockDrillConductedOnDate",
        "",
        function () {
          const [{ value: { mockDrillConductedOn } = null } = {}] =
            this.from || [];

          if (!mockDrillConductedOn) {
            return this.createError({
              message: "Mockdrill Conducted on date is required",
            });
          }

          if (mockDrillConductedOn.isAfter(moment())) {
            return this.createError({
              message: "Please enter a Past date",
            });
          }

          return true;
        }
      ),
      nextMockDrillDueOn: Yup.date().test(
        "isValidNextMockDrillDueOnDate",
        "",
        function () {
          const [
            { value: { mockDrillConductedOn, nextMockDrillDueOn } = {} } = {},
          ] = this.from || [];

          if (!nextMockDrillDueOn?.isValid()) {
            return this.createError({
              message: "Next mockdrill date is required",
            });
          }
          if (!mockDrillConductedOn) return true;
          if (
            isFirstHalf(mockDrillConductedOn) &&
            isFirstHalf(nextMockDrillDueOn)
          ) {
            return this.createError({
              message: "Next mock drill date must be between July and December",
            });
          }
          if (
            isSecondHalf(mockDrillConductedOn) &&
            isSecondHalf(nextMockDrillDueOn)
          ) {
            return this.createError({
              message: "Next mock drill date must be between January and June",
            });
          }
          if (
            mockDrillConductedOn &&
            mockDrillConductedOn.isAfter(nextMockDrillDueOn)
          ) {
            return this.createError({
              message:
                "Next mockdrill date should be after Mockdrill Conducted on date",
            });
          }
          return true;
        }
      ),
    }),
    onSubmit: async (values, { resetForm }) => {
      const { mockDrillConductedOn, nextMockDrillDueOn, ...rest } = values;
      const payload = {
        ...rest,
        mockDrillConductedOn: mockDrillConductedOn.format("YYYY-MM-DD"),
        nextMockDrillDueOn: nextMockDrillDueOn.format("YYYY-MM-DD"),
      };
      try {
        await scheduleMockDrill(payload);
        handleClose();
        resetForm();
      } catch (error) {}
    },
  });

  const {
    handleSubmit,
    values: { mockDrillConductedOn },
  } = formik;

  useEffect(() => {
    try {
      if (id) {
        (async () => {
          await getMockDrillDetail(id);
        })();
      }
    } catch (error) {}
  }, [id]);

  if (isBuildingDataLoading || isMockDrillDataLoading) {
    return <Backdrop open />;
  }

  return (
    <Modal
      handleClose={handleClose}
      isOpen={isOpen}
      maxWidth="sm"
      title={t("Add Mock Drill")}
    >
      <Grid container spacing={2} className="mt-2">
        <Grid item xs={12}>
          <SelectField
            label={t("Building Name") + "*"}
            name="developerBuildingId"
            formik={formik}
            options={updatedBuildingOptions || []}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <DatePicker
            label={t("Mock Drill Conducted On") + "*"}
            name="mockDrillConductedOn"
            formik={formik}
            disableFuture
            format="DD-MM-YYYY"
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <DatePicker
            label={t("Next Mock Drill Due On") + "*"}
            name="nextMockDrillDueOn"
            formik={formik}
            minDate={moment(mockDrillConductedOn)}
            format="DD-MM-YYYY"
            shouldDisableDate={(date) => {
              if (!mockDrillConductedOn) return false;
              if (isFirstHalf(mockDrillConductedOn)) {
                return isFirstHalf(date);
              }
              if (isSecondHalf(mockDrillConductedOn)) {
                return isSecondHalf(date);
              }
              return false;
            }}
          />
        </Grid>

        <Grid item xs={12} className="d-flex justify-content-end gap-2 mt-2">
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isMockDrillScheduleLoading}
            startIcon={
              isMockDrillScheduleLoading && <CircularProgress size={20} />
            }
          >
            {t("Submit")}
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};

MockDrillScheduleModal.propTypes = {
  isOpen: PropTypes.bool,
  handleClose: PropTypes.func,
  userId: PropTypes.number,
  t: PropTypes.func,
};

export default withTranslation()(MockDrillScheduleModal);
